import { Shield, Target, Heart, Zap, Smile, Sword } from "lucide-react";

const MASKY_LOGO = "https://customer-assets.emergentagent.com/job_1abea0ec-cded-4678-a29f-de28c813ea65/artifacts/nv7abkas_masky%20logo.png";

const benefits = [
  { icon: Shield, title: "Builds Confidence", description: "Watch your child grow in self-belief with every session", color: "bg-[#0A1A2F]" },
  { icon: Target, title: "Improves Focus", description: "Learn to concentrate and think strategically", color: "bg-[#D4A857]" },
  { icon: Heart, title: "Encourages Resilience", description: "Develop a never-give-up attitude", color: "bg-[#3FA9F5]" },
  { icon: Zap, title: "Great for Energetic Kids", description: "Channel all that energy into something amazing", color: "bg-[#0A1A2F]" },
  { icon: Smile, title: "Perfect for Shy Children", description: "A gentle, supportive environment to come out of their shell", color: "bg-[#D4A857]" },
];

export default function WhyFencingSection() {
  return (
    <section
      id="why-fencing"
      className="py-20 bg-white"
      data-testid="why-fencing-section"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-[#D4A857]/10 text-[#D4A857] px-4 py-2 rounded text-sm font-bold uppercase tracking-wide mb-4 font-['Montserrat']">
            <Sword size={16} />
            Real Olympic-Style Sword Fencing!
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#0A1A2F] mb-4 font-['Montserrat']">
            Why <span className="text-[#D4A857]">Fencing?</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto font-['Lato']">
            Fencing isn't just a sport — it's a confidence-building, focus-enhancing, 
            friend-making adventure with <span className="font-bold text-[#0A1A2F]">real Olympic-style swords!</span>
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Benefits */}
          <div className="grid sm:grid-cols-2 gap-4">
            {benefits.map((benefit, i) => (
              <div
                key={i}
                className="bg-white rounded-lg p-5 border-2 border-[#F4F4F4] hover:border-[#D4A857]/30 hover:shadow-lg transition-all group"
                data-testid={`benefit-${i}`}
              >
                <div className={`${benefit.color} w-12 h-12 rounded-lg flex items-center justify-center text-white mb-3 group-hover:scale-110 transition-transform`}>
                  <benefit.icon size={24} />
                </div>
                <h3 className="text-lg font-bold text-[#0A1A2F] mb-1 font-['Montserrat']">{benefit.title}</h3>
                <p className="text-gray-600 text-sm font-['Lato']">{benefit.description}</p>
              </div>
            ))}
            
            {/* Masky Card - Small */}
            <div className="bg-[#0A1A2F] rounded-lg p-5 text-white flex flex-col justify-center items-center text-center">
              <img
                src="https://customer-assets.emergentagent.com/job_1abea0ec-cded-4678-a29f-de28c813ea65/artifacts/2pfnnew9_bigger%20masky.png"
                alt="Masky thumbs up"
                className="w-32 mb-3 masky-glow"
              />
              <img src={MASKY_LOGO} alt="MASKY" className="h-7 mb-1 masky-glow-sm" />
              <p className="font-bold text-[#D4A857] mb-1 font-['Montserrat'] text-xs uppercase">says:</p>
              <p className="text-white/90 text-xs font-['Lato']">
                "Every child I meet leaves a little braver!"
              </p>
            </div>
          </div>

          {/* Fencing Image */}
          <div className="relative">
            <div className="rounded-lg overflow-hidden shadow-2xl border-4 border-[#F4F4F4]">
              <img
                src="https://customer-assets.emergentagent.com/job_fencing-kids-pass/artifacts/ijlhh2l2_338492_10150721397297995_1691977766_o%20%281%29.jpg"
                alt="Child fencing with foil"
                className="w-full h-auto object-cover"
              />
            </div>
            
            {/* Overlay Badge */}
            <div className="absolute -bottom-4 -right-4 bg-[#D4A857] text-[#0A1A2F] px-6 py-3 rounded-lg shadow-lg font-['Montserrat']">
              <p className="font-bold">Real Olympic Fencing</p>
              <p className="text-sm font-normal">Foil discipline</p>
            </div>
          </div>
        </div>

        {/* Parent Note */}
        <div className="mt-12 bg-[#F4F4F4] rounded-lg p-8 text-center">
          <p className="text-[#0A1A2F] text-lg leading-relaxed max-w-3xl mx-auto font-['Lato']">
            <span className="font-bold">For parents:</span> Your child will be learning <span className="text-[#D4A857] font-semibold">foil fencing</span> — 
            a real Olympic sword discipline. They'll use proper fencing swords and our full electric scoring system. 
            Fencing is equally great for <span className="text-[#3FA9F5] font-semibold">high-energy kids who need an outlet</span> 
            {" "}and{" "}
            <span className="text-[#3FA9F5] font-semibold">quieter children who need a gentle confidence boost</span>.
          </p>
        </div>
      </div>
    </section>
  );
}
